// src/pages/Login.js
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import FrontHeader from '../components/common/FrontHeader';
import FrontFooter from '../components/common/FrontFooter';
import './Login.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { login, googleLogin } = useAuth();  // ✅ use AuthContext

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password');
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.success) {
        // AuthContext already stored user & token, navigate directly
        if (result.user.role === 'admin') {
          navigate('/admin/dashboard');
        } else if (result.user.role === 'service-provider') {
          navigate('/service-provider/dashboard');
        } else {
          navigate('/owner/dashboard');
        }
      } else {
        if (result.needsVerification) {
          setError('Please verify your email first. Check your inbox for the verification code.');
          setTimeout(() => navigate('/verify-email', { state: { email } }), 2000);
        } else {
          setError(result.message || 'Login failed');
        }
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError('');
    setIsLoading(true);
    try {
      const result = await googleLogin(credentialResponse.credential);
      if (result.success) {
        if (result.needsProfileCompletion) {
          navigate('/complete-profile', {
            state: { userData: result.user, token: localStorage.getItem('marilink_token') }
          });
        } else {
          if (result.user.role === 'admin') navigate('/admin/dashboard');
          else if (result.user.role === 'service-provider') navigate('/service-provider/dashboard');
          else navigate('/owner/dashboard');
        }
      } else {
        setError(result.message || 'Google login failed. Please try again.');
      }
    } catch (err) {
      setError('Google login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  return (
    <div className="login-page">
      <FrontHeader />
      <div className="login-main">
        <div className="login-container">
          <div className="login-header">
            <h1>Welcome back</h1>
            <p className="subtitle">Sign in to your account</p>
          </div>

          {error && (
            <div className="error-alert">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}

          <div className="google-login-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              useOneTap
              theme="filled_blue"
              shape="pill"
              text="continue_with"
              size="large"
            />
          </div>

          <div className="divider">
            <span>or continue with email</span>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label><i className="fas fa-envelope"></i> Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>

            <div className="form-group">
              <label><i className="fas fa-lock"></i> Password</label>
              <div className="password-input">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <i className={`fas fa-${showPassword ? 'eye-slash' : 'eye'}`}></i>
                </button>
              </div>
            </div>

            <div className="form-options">
              <label className="checkbox-label">
                <input type="checkbox" />
                <span>Remember me</span>
              </label>
              <Link to="/forgot-password" className="forgot-link">Forgot password?</Link>
            </div>

            <button type="submit" className="login-btn" disabled={isLoading}>
              {isLoading ? <><i className="fas fa-spinner fa-spin"></i> Signing in...</> : 'Sign in'}
            </button>
          </form>

          <p className="register-link">
            Don't have an account? <Link to="/register">Create account</Link>
          </p>
        </div>
      </div>
      <FrontFooter />
    </div>
  );
};

export default Login;